import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";

interface AssetTypeFieldsProps {
  assetType: string;
  formData: Record<string, any>;
  onChange: (field: string, value: any) => void;
  unaMembers?: Array<{ id: string; user_id: string; member_name: string; member_role: string }>;
}

const US_STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut", "Delaware",
  "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky",
  "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota", "Mississippi",
  "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey", "New Mexico",
  "New York", "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania",
  "Rhode Island", "South Carolina", "South Dakota", "Tennessee", "Texas", "Utah", "Vermont",
  "Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming"
];

export function AssetTypeFields({ assetType, formData, onChange, unaMembers = [] }: AssetTypeFieldsProps) {
  // Investment/Brokerage Account Fields
  if (assetType === 'Investment/Brokerage Account') {
    return (
      <div className="space-y-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <div className="space-y-2">
              <p className="font-semibold">Non-Retirement Accounts Only</p>
              <p className="text-sm">
                This workflow is for non-retirement investment and brokerage accounts. 
                Retirement accounts (401k, IRA, Roth IRA) cannot be transferred without 
                significant tax penalties and require special handling.
              </p>
            </div>
          </AlertDescription>
        </Alert>

        <div>
          <Label htmlFor="account_type">Account Type *</Label>
          <Select value={formData.account_type || ''} onValueChange={(v) => onChange('account_type', v)}>
            <SelectTrigger>
              <SelectValue placeholder="Select account type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Individual Brokerage">Individual Brokerage Account</SelectItem>
              <SelectItem value="Joint Brokerage">Joint Brokerage Account</SelectItem>
              <SelectItem value="Investment Account">Investment Account</SelectItem>
              <SelectItem value="Trust Account">Trust Account</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <Checkbox
              id="is_retirement_account"
              checked={formData.is_retirement_account || false}
              onCheckedChange={(checked) => onChange('is_retirement_account', checked)}
            />
            <Label htmlFor="is_retirement_account" className="font-normal cursor-pointer">
              This is a retirement account (401k, IRA, Roth IRA, SEP IRA, SIMPLE IRA, etc.)
            </Label>
          </div>

          {formData.is_retirement_account && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <p className="font-semibold mb-2">Retirement Accounts Cannot Be Transferred</p>
                <p className="text-sm">
                  Retirement accounts cannot be transferred to a UNA without triggering 
                  immediate taxation and early withdrawal penalties. You would need to:
                </p>
                <ul className="list-disc ml-5 mt-2 space-y-1 text-sm">
                  <li>Liquidate the account (triggering taxes and penalties)</li>
                  <li>Pay all taxes and penalties owed</li>
                  <li>Then contribute the remaining cash to your UNA</li>
                </ul>
                <p className="mt-2 font-semibold text-sm">
                  Please consult with a financial advisor before proceeding. 
                  This workflow cannot be completed for retirement accounts.
                </p>
              </AlertDescription>
            </Alert>
          )}
        </div>

        {!formData.is_retirement_account && (
          <>
            <div>
              <Label htmlFor="brokerage_firm">Brokerage Firm/Institution *</Label>
              <Input
                id="brokerage_firm"
                value={formData.brokerage_firm || ''}
                onChange={(e) => onChange('brokerage_firm', e.target.value)}
                placeholder="e.g., Fidelity, Vanguard, Charles Schwab"
              />
            </div>

            <div>
              <Label htmlFor="account_number">Account Number (Optional)</Label>
              <Input
                id="account_number"
                type="text"
                value={formData.account_number || ''}
                onChange={(e) => onChange('account_number', e.target.value)}
                placeholder="Last 4 digits only recommended for privacy"
              />
              <p className="text-sm text-muted-foreground mt-1">
                For privacy, only provide last 4 digits. Full account details can be recorded elsewhere.
              </p>
            </div>

            <div>
              <Label htmlFor="account_description">Account Description *</Label>
              <Textarea
                id="account_description"
                value={formData.account_description || ''}
                onChange={(e) => onChange('account_description', e.target.value)}
                placeholder="Describe the account holdings, investment strategy, or purpose"
                rows={3}
              />
              <p className="text-sm text-muted-foreground mt-1">
                e.g., "Diversified stock portfolio focused on dividend growth" or "Index fund portfolio for long-term growth"
              </p>
            </div>

            <div>
              <Label htmlFor="estimated_holdings">Estimated Holdings (Optional)</Label>
              <Input
                id="estimated_holdings"
                value={formData.estimated_holdings || ''}
                onChange={(e) => onChange('estimated_holdings', e.target.value)}
                placeholder="General description, not dollar amount"
              />
              <p className="text-sm text-muted-foreground mt-1">
                e.g., "Mid-sized portfolio" or "Substantial holdings" - avoid specific dollar amounts to maintain privacy
              </p>
            </div>

            <div>
              <Label htmlFor="investment_contribution_purpose">Contribution Purpose</Label>
              <Textarea
                id="investment_contribution_purpose"
                value={formData.investment_contribution_purpose || ''}
                onChange={(e) => onChange('investment_contribution_purpose', e.target.value)}
                placeholder="How will the UNA steward these investment assets?"
                rows={3}
              />
            </div>
          </>
        )}
      </div>
    );
  }

  // Precious Metals Fields
  if (assetType === 'Precious Metals') {
    return (
      <div className="space-y-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <div className="space-y-2">
              <p className="font-semibold">Physical Precious Metals</p>
              <p className="text-sm">
                Document the contribution of physical precious metals to your UNA. 
                This includes coins, bars, bullion, and other forms.
              </p>
            </div>
          </AlertDescription>
        </Alert>

        <div>
          <Label htmlFor="metal_type">Metal Type *</Label>
          <Select value={formData.metal_type || ''} onValueChange={(v) => onChange('metal_type', v)}>
            <SelectTrigger>
              <SelectValue placeholder="Select metal type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Gold">Gold</SelectItem>
              <SelectItem value="Silver">Silver</SelectItem>
              <SelectItem value="Platinum">Platinum</SelectItem>
              <SelectItem value="Palladium">Palladium</SelectItem>
              <SelectItem value="Mixed Precious Metals">Mixed Precious Metals</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="metal_form">Form *</Label>
          <Select value={formData.metal_form || ''} onValueChange={(v) => onChange('metal_form', v)}>
            <SelectTrigger>
              <SelectValue placeholder="Select form" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Coins">Coins</SelectItem>
              <SelectItem value="Bars/Bullion">Bars/Bullion</SelectItem>
              <SelectItem value="Rounds">Rounds</SelectItem>
              <SelectItem value="Jewelry">Jewelry</SelectItem>
              <SelectItem value="Mixed Forms">Mixed Forms</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="metal_description">Detailed Description *</Label>
          <Textarea
            id="metal_description"
            value={formData.metal_description || ''}
            onChange={(e) => onChange('metal_description', e.target.value)}
            placeholder="Provide detailed description of the precious metals being contributed"
            rows={4}
          />
          <p className="text-sm text-muted-foreground mt-1">
            Include: quantity, weight (troy ounces if applicable), denominations, minting details, 
            purity/fineness, any notable characteristics
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            Example: "25 American Gold Eagles (1 oz each), various dates 2015-2024, .9167 fine gold"
          </p>
        </div>

        <div>
          <Label htmlFor="metal_location">Current Location *</Label>
          <Input
            id="metal_location"
            value={formData.metal_location || ''}
            onChange={(e) => onChange('metal_location', e.target.value)}
            placeholder="Where are the precious metals currently located?"
          />
          <p className="text-sm text-muted-foreground mt-1">
            General location only - e.g., "Home safe", "Bank vault", "Storage facility"
          </p>
        </div>

        <div>
          <Label htmlFor="storage_type">Storage Type *</Label>
          <Select value={formData.storage_type || ''} onValueChange={(v) => onChange('storage_type', v)}>
            <SelectTrigger>
              <SelectValue placeholder="Select storage type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Home Safe">Home Safe</SelectItem>
              <SelectItem value="Bank Safe Deposit Box">Bank Safe Deposit Box</SelectItem>
              <SelectItem value="Third-Party Vault">Third-Party Vault/Storage Facility</SelectItem>
              <SelectItem value="Private Storage">Private Storage</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="metal_purpose">Purpose of Contribution</Label>
          <Textarea
            id="metal_purpose"
            value={formData.metal_purpose || ''}
            onChange={(e) => onChange('metal_purpose', e.target.value)}
            placeholder="How will the UNA steward these precious metals?"
            rows={3}
          />
          <p className="text-sm text-muted-foreground mt-1">
            e.g., "Long-term wealth preservation", "Hedge against inflation", "Multi-generational asset"
          </p>
        </div>

        <div>
          <Label htmlFor="transfer_logistics">Physical Transfer Logistics (Optional)</Label>
          <Textarea
            id="transfer_logistics"
            value={formData.transfer_logistics || ''}
            onChange={(e) => onChange('transfer_logistics', e.target.value)}
            placeholder="Any special considerations for physically transferring these metals?"
            rows={2}
          />
          <p className="text-sm text-muted-foreground mt-1">
            e.g., Transport arrangements, insurance requirements, witness needs
          </p>
        </div>
      </div>
    );
  }

  // Real Estate Fields
  if (assetType?.includes("Real Estate")) {
    return (
      <div className="space-y-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <div className="space-y-2">
              <p className="font-semibold">Important: Cash-Flowing Rental Properties</p>
              <p className="text-sm">
                If this property generates rental income, it should be titled to an LLC 
                owned by your UNA (not directly to the UNA). This provides liability protection 
                and cleaner accounting for the rental business.
              </p>
              <div className="mt-2">
                <p className="text-sm font-semibold">Process for rental properties:</p>
                <ol className="list-decimal ml-5 mt-1 space-y-1 text-sm">
                  <li>First, establish an LLC owned by your UNA</li>
                  <li>Then, title the rental property to that LLC</li>
                </ol>
              </div>
            </div>
          </AlertDescription>
        </Alert>

        <div className="space-y-4">
          <div>
            <Label htmlFor="property_address">Property Address *</Label>
            <Input
              id="property_address"
              value={formData.property_address || ''}
              onChange={(e) => onChange('property_address', e.target.value)}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="property_city">City *</Label>
              <Input
                id="property_city"
                value={formData.property_city || ''}
                onChange={(e) => onChange('property_city', e.target.value)}
                required
              />
            </div>

            <div>
              <Label htmlFor="property_state">State *</Label>
              <Select value={formData.property_state || ''} onValueChange={(v) => onChange('property_state', v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select state" />
                </SelectTrigger>
                <SelectContent>
                  {US_STATES.map(state => (
                    <SelectItem key={state} value={state}>{state}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="property_zip">ZIP Code *</Label>
              <Input
                id="property_zip"
                value={formData.property_zip || ''}
                onChange={(e) => onChange('property_zip', e.target.value)}
                required
              />
            </div>

            <div>
              <Label htmlFor="property_county">County *</Label>
              <Input
                id="property_county"
                value={formData.property_county || ''}
                onChange={(e) => onChange('property_county', e.target.value)}
                required
                placeholder="For recording purposes"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="property_type_detail">Property Type *</Label>
            <Select value={formData.property_type_detail || ''} onValueChange={(v) => onChange('property_type_detail', v)}>
              <SelectTrigger>
                <SelectValue placeholder="Select property type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Primary Residence">Primary Residence</SelectItem>
                <SelectItem value="Secondary Residence">Secondary Residence</SelectItem>
                <SelectItem value="Rental Property">Rental Property</SelectItem>
                <SelectItem value="Commercial Property">Commercial Property</SelectItem>
                <SelectItem value="Vacant Land">Vacant Land</SelectItem>
                <SelectItem value="Agricultural">Agricultural</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="property_description">Property Description / Legal Description</Label>
            <Textarea
              id="property_description"
              value={formData.property_description || ''}
              onChange={(e) => onChange('property_description', e.target.value)}
              rows={3}
              placeholder="Include legal description if available"
            />
          </div>

          <div className="flex items-start space-x-2 pt-2">
            <Checkbox
              id="has_mortgage"
              checked={formData.has_mortgage || false}
              onCheckedChange={(checked) => onChange('has_mortgage', checked)}
            />
            <Label htmlFor="has_mortgage" className="font-normal">Property has a mortgage</Label>
          </div>

          {formData.has_mortgage && (
            <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
              <div>
                <Label htmlFor="mortgage_lender">Lender Name</Label>
                <Input
                  id="mortgage_lender"
                  value={formData.mortgage_lender || ''}
                  onChange={(e) => onChange('mortgage_lender', e.target.value)}
                />
              </div>

              <div className="flex items-start space-x-2">
                <Checkbox
                  id="lender_approval_obtained"
                  checked={formData.lender_approval_obtained || false}
                  onCheckedChange={(checked) => onChange('lender_approval_obtained', checked)}
                />
                <Label htmlFor="lender_approval_obtained" className="font-normal">
                  Lender approval obtained *
                </Label>
              </div>

              {formData.has_mortgage && !formData.lender_approval_obtained && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Lender approval is required before contributing mortgaged property
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Vehicle Fields
  if (assetType?.includes("Vehicle")) {
    return (
      <div className="space-y-6">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="vehicle_year">Year *</Label>
              <Select value={formData.vehicle_year?.toString() || ''} onValueChange={(v) => onChange('vehicle_year', parseInt(v))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select year" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: new Date().getFullYear() - 1989 }, (_, i) => new Date().getFullYear() - i).map(year => (
                    <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="vehicle_type">Vehicle Type *</Label>
              <Select value={formData.vehicle_type || ''} onValueChange={(v) => onChange('vehicle_type', v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Car">Car</SelectItem>
                  <SelectItem value="Truck">Truck</SelectItem>
                  <SelectItem value="SUV">SUV</SelectItem>
                  <SelectItem value="Van">Van</SelectItem>
                  <SelectItem value="Motorcycle">Motorcycle</SelectItem>
                  <SelectItem value="RV">RV</SelectItem>
                  <SelectItem value="Boat">Boat</SelectItem>
                  <SelectItem value="Trailer">Trailer</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="vehicle_make">Make *</Label>
            <Input
              id="vehicle_make"
              value={formData.vehicle_make || ''}
              onChange={(e) => onChange('vehicle_make', e.target.value)}
              required
            />
          </div>

          <div>
            <Label htmlFor="vehicle_model">Model *</Label>
            <Input
              id="vehicle_model"
              value={formData.vehicle_model || ''}
              onChange={(e) => onChange('vehicle_model', e.target.value)}
              required
            />
          </div>

          <div>
            <Label htmlFor="vehicle_vin">VIN *</Label>
            <Input
              id="vehicle_vin"
              value={formData.vehicle_vin || ''}
              onChange={(e) => onChange('vehicle_vin', e.target.value)}
              maxLength={17}
              placeholder="17-character VIN"
              required
            />
          </div>

          <div>
            <Label htmlFor="vehicle_color">Color *</Label>
            <Input
              id="vehicle_color"
              value={formData.vehicle_color || ''}
              onChange={(e) => onChange('vehicle_color', e.target.value)}
              placeholder="e.g., Red, Blue, Silver"
              required
            />
          </div>

          <div>
            <Label htmlFor="current_title_state">Title State *</Label>
            <Select value={formData.current_title_state || ''} onValueChange={(v) => onChange('current_title_state', v)}>
              <SelectTrigger>
                <SelectValue placeholder="Select state" />
              </SelectTrigger>
              <SelectContent>
                {US_STATES.map(state => (
                  <SelectItem key={state} value={state}>{state}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-start space-x-2 pt-2">
            <Checkbox
              id="has_lien"
              checked={formData.has_lien || false}
              onCheckedChange={(checked) => onChange('has_lien', checked)}
            />
            <Label htmlFor="has_lien" className="font-normal">Vehicle has a lien</Label>
          </div>

          {formData.has_lien && (
            <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
              <div>
                <Label htmlFor="lienholder_name">Lienholder Name</Label>
                <Input
                  id="lienholder_name"
                  value={formData.lienholder_name || ''}
                  onChange={(e) => onChange('lienholder_name', e.target.value)}
                />
              </div>

              <div className="flex items-start space-x-2">
                <Checkbox
                  id="lienholder_approval_obtained"
                  checked={formData.lienholder_approval_obtained || false}
                  onCheckedChange={(checked) => onChange('lienholder_approval_obtained', checked)}
                />
                <Label htmlFor="lienholder_approval_obtained" className="font-normal">
                  Lienholder approval obtained *
                </Label>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Cash/Bank Account Fields
  if (assetType === "Cash/Bank Account") {
    return (
      <div className="space-y-4">
        <div>
          <Label htmlFor="contribution_description">Contribution Description *</Label>
          <Input
            id="contribution_description"
            value={formData.contribution_description || ''}
            onChange={(e) => onChange('contribution_description', e.target.value)}
            placeholder="e.g., Initial capital contribution"
            required
          />
        </div>

        <div>
          <Label htmlFor="account_type">Account Type</Label>
          <Select value={formData.account_type || ''} onValueChange={(v) => onChange('account_type', v)}>
            <SelectTrigger>
              <SelectValue placeholder="Select account type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Checking">Checking</SelectItem>
              <SelectItem value="Savings">Savings</SelectItem>
              <SelectItem value="Money Market">Money Market</SelectItem>
              <SelectItem value="Investment Account">Investment Account</SelectItem>
              <SelectItem value="Cash">Cash</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="contribution_purpose">Contribution Purpose</Label>
          <Textarea
            id="contribution_purpose"
            value={formData.contribution_purpose || ''}
            onChange={(e) => onChange('contribution_purpose', e.target.value)}
            rows={3}
          />
        </div>

        <Alert className="bg-blue-50 border-blue-200">
          <AlertDescription className="text-sm">
            Note: We do not collect dollar amounts. This maintains privacy and proper ecclesiastical jurisdiction.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Personal Property Fields
  if (assetType === "Personal Property") {
    return (
      <div className="space-y-4">
        <div>
          <Label htmlFor="property_description">Property Description *</Label>
          <Textarea
            id="property_description"
            value={formData.property_description || ''}
            onChange={(e) => onChange('property_description', e.target.value)}
            rows={4}
            placeholder="Detailed description of the property"
            required
          />
        </div>

        <div>
          <Label htmlFor="property_category">Category *</Label>
          <Select value={formData.property_category || ''} onValueChange={(v) => onChange('property_category', v)}>
            <SelectTrigger>
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Furniture & Furnishings">Furniture & Furnishings</SelectItem>
              <SelectItem value="Equipment & Tools">Equipment & Tools</SelectItem>
              <SelectItem value="Electronics">Electronics</SelectItem>
              <SelectItem value="Artwork & Collectibles">Artwork & Collectibles</SelectItem>
              <SelectItem value="Jewelry & Valuables">Jewelry & Valuables</SelectItem>
              <SelectItem value="Library/Books">Library/Books</SelectItem>
              <SelectItem value="Musical Instruments">Musical Instruments</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="property_condition">Condition</Label>
          <Select value={formData.property_condition || ''} onValueChange={(v) => onChange('property_condition', v)}>
            <SelectTrigger>
              <SelectValue placeholder="Select condition" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="New">New</SelectItem>
              <SelectItem value="Excellent">Excellent</SelectItem>
              <SelectItem value="Good">Good</SelectItem>
              <SelectItem value="Fair">Fair</SelectItem>
              <SelectItem value="As-Is">As-Is</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="property_purpose">How Will UNA Steward This?</Label>
          <Textarea
            id="property_purpose"
            value={formData.property_purpose || ''}
            onChange={(e) => onChange('property_purpose', e.target.value)}
            rows={2}
          />
        </div>
      </div>
    );
  }

  // Default for other asset types
  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="asset_description">Asset Description *</Label>
        <Textarea
          id="asset_description"
          value={formData.asset_description || formData.other_asset_description || ''}
          onChange={(e) => onChange(assetType === "Other Asset" ? 'other_asset_description' : 'asset_description', e.target.value)}
          rows={4}
          placeholder="Detailed description of the asset"
          required
        />
      </div>

      <div>
        <Label htmlFor="asset_purpose">Asset Purpose</Label>
        <Textarea
          id="asset_purpose"
          value={formData.asset_purpose || formData.other_asset_purpose || ''}
          onChange={(e) => onChange(assetType === "Other Asset" ? 'other_asset_purpose' : 'asset_purpose', e.target.value)}
          rows={2}
          placeholder="How will the UNA steward this asset?"
        />
      </div>
    </div>
  );
}